import { computed, ref, watch, Ref } from 'vue'
import type { ComposerTranslation } from 'vue-i18n'
import { IS_PRIVATE } from '@/common/const/env'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { downloadByUrl } from '@/utils/tool'
import { DEPLOY_PACKAGE_ICONS } from '../../deploy-package-icons'
import {
  buildLinuxDistroTabEntries,
  cornerIconForLinuxSystem,
} from './deploy-package-download.linux'
import type {
  DeployDownloadSource,
  DeployOsNavItem,
  DeployPackageItem,
} from './deploy-package-download.types'

export function useDeployPackageDownload(
  data: Ref<DeployDownloadSource>,
  t: ComposerTranslation,
  locale: Ref<string>,
) {
  const selectedOS = ref('windows')
  const selectedLinuxTabId = ref('')

  const linuxDistroList = computed(() => data.value.systems || [])
  const linuxDistroTabEntries = computed(() => {
    void locale.value
    return buildLinuxDistroTabEntries(linuxDistroList.value)
  })

  function syncSelectedLinuxTab() {
    const entries = linuxDistroTabEntries.value
    if (!entries.length) {
      selectedLinuxTabId.value = ''
      return
    }
    const cur = selectedLinuxTabId.value
    if (cur && entries.some(e => e.tabId === cur)) return
    selectedLinuxTabId.value = entries[0].tabId
  }

  watch(linuxDistroTabEntries, () => syncSelectedLinuxTab(), { immediate: true })

  watch(selectedOS, (os) => {
    if (os === 'linux') {
      syncSelectedLinuxTab()
    } else {
      selectedLinuxTabId.value = ''
    }
  })

  if (IS_JP_ONLY_BUILD && (selectedOS.value === 'macos' || selectedOS.value === 'linux')) {
    selectedOS.value = 'windows'
  }
  if (IS_PRIVATE && selectedOS.value === 'macos') {
    selectedOS.value = 'windows'
  }

  const osList = computed((): DeployOsNavItem[] => {
    void locale.value
    const winVer = IS_JP_ONLY_BUILD
      ? t('terminalDeploy.osVersionWindowsJaOnly')
      : 'Windows XP / 2003 / 7 / 10 / 11'
    const serverVer = IS_JP_ONLY_BUILD
      ? t('terminalDeploy.osVersionWinServerJaOnly')
      : 'Windows Server 2008 / 2012 / 2016+'
    const base: DeployOsNavItem[] = [
      {
        type: 'windows',
        name: t('terminalDeploy.osNavWindows'),
        version: winVer,
        icon: DEPLOY_PACKAGE_ICONS.windows,
      },
      {
        type: 'win-server',
        name: t('terminalDeploy.osNavWinServer'),
        version: serverVer,
        icon: DEPLOY_PACKAGE_ICONS.winServer,
      },
    ]
    if (IS_JP_ONLY_BUILD) {
      return base
    }
    const extra: DeployOsNavItem[] = []
    if (!IS_PRIVATE) {
      extra.push({
        type: 'macos',
        name: t('terminalDeploy.osNavMacOS'),
        version: 'Intel & Apple Silicon',
        icon: DEPLOY_PACKAGE_ICONS.macos,
      })
    }
    extra.push({
      type: 'linux',
      name: t('terminalDeploy.osNavLinux'),
      version: t('terminalDeploy.linuxVersionSubtitle'),
      icon: DEPLOY_PACKAGE_ICONS.linux,
    })
    return [...base, ...extra]
  })

  function latestVersionLabel(url: string) {
    return t('terminalDeploy.latestVersion')
  }

  function normalizeLinuxPackages(): DeployPackageItem[] {
    const list: DeployPackageItem[] = []
    const map = data.value.linux_download_map
    const tab = linuxDistroTabEntries.value.find(e => e.tabId === selectedLinuxTabId.value)
    if (!tab?.mapKeys.length) return list
    const pkgWord = t('terminalDeploy.installPackage')
    tab.mapKeys.forEach(system => {
      const val = map[system]
      const linuxIcon = cornerIconForLinuxSystem(system)
      const cornerIconWide = system.startsWith('统信系统')
      if (typeof val === 'string' && val) {
        list.push({
          title: `${system} ${pkgWord}`.trim(),
          version: latestVersionLabel(val),
          url: val,
          terminalType: TerminalType.linux,
          cornerIcon: linuxIcon,
          cornerIconWide,
        })
      } else if (val && typeof val === 'object' && !Array.isArray(val)) {
        const obj = val as Record<string, string>
        Object.entries(obj).forEach(([arch, url]) => {
          if (url) {
            list.push({
              title: `${system} (${arch}) ${pkgWord}`.trim(),
              version: latestVersionLabel(url),
              url,
              terminalType: TerminalType.linux,
              cornerIcon: linuxIcon,
              cornerIconWide,
            })
          }
        })
      }
    })
    return list
  }

  function getDownloadPackages(): DeployPackageItem[] {
    const d = data.value
    switch (selectedOS.value) {
      case 'windows': {
        const pkgs: DeployPackageItem[] = []
        if (d.windows_download_url) {
          pkgs.push({
            title: t('terminalDeploy.windowsPackage'),
            version: latestVersionLabel(d.windows_download_url),
            url: d.windows_download_url,
            terminalType: TerminalType.windows,
            cornerIcon: DEPLOY_PACKAGE_ICONS.windows,
          })
        }
        if (!IS_JP_ONLY_BUILD && d.windows_xp_download_url) {
          pkgs.push({
            title: t('terminalDeploy.windowsXPPackage'),
            version: latestVersionLabel(d.windows_xp_download_url),
            url: d.windows_xp_download_url,
            terminalType: TerminalType.windows,
            cornerIcon: DEPLOY_PACKAGE_ICONS.windows,
          })
        }
        return pkgs
      }
      case 'win-server': {
        const pkgs: DeployPackageItem[] = []
        if (d.win_server_download_url) {
          pkgs.push({
            title: t('terminalDeploy.windowsServerPackage'),
            version: latestVersionLabel(d.win_server_download_url),
            url: d.win_server_download_url,
            terminalType: TerminalType.windowsServer,
            cornerIcon: DEPLOY_PACKAGE_ICONS.winServer,
          })
        }
        if (!IS_JP_ONLY_BUILD && d.windows_server2003_download_url) {
          pkgs.push({
            title: t('terminalDeploy.windowsServer2003Package'),
            version: latestVersionLabel(d.windows_server2003_download_url),
            url: d.windows_server2003_download_url,
            terminalType: TerminalType.windowsServer,
            cornerIcon: DEPLOY_PACKAGE_ICONS.winServer,
          })
        }
        return pkgs
      }
      case 'macos': {
        if (IS_PRIVATE) return []
        const pkgs: DeployPackageItem[] = []
        if (d.macos_arm64_download_url) {
          pkgs.push({
            title: t('terminalDeploy.appleChipPackage'),
            version: latestVersionLabel(d.macos_arm64_download_url),
            url: d.macos_arm64_download_url,
            terminalType: TerminalType.mac,
            cornerIcon: DEPLOY_PACKAGE_ICONS.macos,
          })
        }
        if (d.macos_amd64_download_url) {
          pkgs.push({
            title: t('terminalDeploy.intelChipPackage'),
            version: latestVersionLabel(d.macos_amd64_download_url),
            url: d.macos_amd64_download_url,
            terminalType: TerminalType.mac,
            cornerIcon: DEPLOY_PACKAGE_ICONS.macos,
          })
        }
        return pkgs
      }
      case 'linux':
        return normalizeLinuxPackages()
      default:
        return []
    }
  }

  const displayDownloadPackages = computed((): DeployPackageItem[] => {
    void locale.value
    if (IS_JP_ONLY_BUILD) {
      const pkgs: DeployPackageItem[] = []
      const d = data.value
      // if (d.win_server_download_url) {
      //   pkgs.push({
      //     title: t('terminalDeploy.windowsServerPackage'),
      //     version: latestVersionLabel(d.win_server_download_url),
      //     url: d.win_server_download_url,
      //     terminalType: TerminalType.windowsServer,
      //     cornerIcon: DEPLOY_PACKAGE_ICONS.winServer,
      //   })
      // }
      if (d.windows_download_url) {
        pkgs.push({
          title: t('terminalDeploy.windowsPackage'),
          version: latestVersionLabel(d.windows_download_url),
          url: d.windows_download_url,
          terminalType: TerminalType.windows,
          cornerIcon: DEPLOY_PACKAGE_ICONS.windows,
        })
      }
      return pkgs
    }
    return getDownloadPackages()
  })

  function handleDownload(url: string) {
    if (!url) return
    downloadByUrl(url)
  }

  return {
    IS_JP_ONLY_BUILD,
    selectedOS,
    selectedLinuxTabId,
    osList,
    linuxDistroTabEntries,
    displayDownloadPackages,
    handleDownload,
  }
}
